import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import KriOverviewApi from '../../../../../../apis/bff-api/central-monitoring/kri-overview';
import Loading from '../../../../../components/core/loading/loading';
import Card from '@components-clean/core/card';
import EditCard from '@components-clean/core/edit-card';
import { InfoField } from '@components-clean/core/info-field';
import { TextArea, TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';

interface IReviewCardProps {
    studyRef: string;
    canEdit: boolean;
    selectedPanelId?: string;
    datasetData?: CM2Dataset;
    comparisonData?: CM2Dataset;
}

interface KriReviewDatasetInFocusForm {
    summary: string;
}
interface IFormValidationConfig {
    summary: {
        maxLength: number;
    };
}
const formValidationConfig: IFormValidationConfig = {
    summary: {
        maxLength: 3000,
    },
};

const EMPTY_FORM: KriReviewDatasetInFocusForm = {
    summary: '',
};

export const COMPONENT_NAME = 'review-content';

export const ReviewContent = ({
    studyRef,
    canEdit,
    selectedPanelId,
    datasetData,
    comparisonData,
}: IReviewCardProps) => {
    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('cm', {
        keyPrefix: 'monitoringToolkit.kriOverview.cards',
    });

    const [isEditing, setIsEditing] = useState(false);

    const [comparisonReviewDefaultMessage, setComparisonReviewDefaultMessage] = useState(() =>
        t('sankey.emptyMessage'),
    );
    const [datasetReviewDefaultMessage, setDatasetReviewDefaultMessage] = useState(() =>
        t('review.panelAndDatasetEmptyMessage'),
    );

    const { data: kriReviewDatasetInFocus, isValidating: isLoadingKriReviewDatasetInFocus } =
        KriOverviewApi.useGetKRIReview(studyRef, datasetData?.id, selectedPanelId);

    const { data: kriReviewComparisonDataset, isValidating: isLoadingKriReviewComparisonDataset } =
        KriOverviewApi.useGetKRIReview(studyRef, comparisonData?.id, selectedPanelId);

    const { trigger: saveReview, isMutating: isSaving } = KriOverviewApi.useSave(
        studyRef,
        datasetData?.id,
        selectedPanelId,
    );

    const isLoading = isLoadingKriReviewDatasetInFocus || isSaving;

    const {
        control,
        formState: { isDirty },
        reset,
        getValues,
    } = useForm<KriReviewDatasetInFocusForm>({ defaultValues: EMPTY_FORM });

    useEffect(() => {
        const data = {
            summary: kriReviewDatasetInFocus?.summary ?? '',
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setIsEditing(false);
        reset(data);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [kriReviewDatasetInFocus]);

    // Update the default message displayed in the review sections based on dropdowns
    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        if (!datasetData && !selectedPanelId) {
            setDatasetReviewDefaultMessage(t('review.panelAndDatasetEmptyMessage'));
        }
        if (datasetData && !selectedPanelId) {
            setDatasetReviewDefaultMessage(t('review.panelEmptyMessage'));
        }
        if (!comparisonData && !selectedPanelId) {
            setComparisonReviewDefaultMessage(t('sankey.emptyMessage'));
        }
        if (!comparisonData && selectedPanelId) {
            setComparisonReviewDefaultMessage(t('review.selectComparisonDataset'));
        }
        if (comparisonData && !selectedPanelId) {
            setComparisonReviewDefaultMessage(t('panel.emptyMessage'));
        }
        if (comparisonData && selectedPanelId) {
            setComparisonReviewDefaultMessage(kriReviewComparisonDataset?.summary ?? t('review.noReview'));
        }
        /* eslint-enable @eslint-react/set-state-in-effect */
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [selectedPanelId, datasetData, comparisonData]);

    const isValid = (form: KriReviewDatasetInFocusForm): boolean => {
        const formValidationErrors: string[] = [];

        if ((form.summary?.length ?? 0) > formValidationConfig.summary.maxLength) {
            formValidationErrors.push(
                `"Review Summary" cannot exceed ${formValidationConfig.summary.maxLength} characters`,
            );
        }

        if (formValidationErrors.length === 0) {
            return true;
        }

        ShowError(`There are form validation errors:${formValidationErrors.map((error) => ` ${error}`).join(', ')}`);
        return false;
    };

    useUnsavedChangesTracking({
        area: 'central-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onSaveHandler = async () => {
        try {
            if (!isValid(getValues())) {
                return;
            }

            const saveSummary = getValues('summary');
            const result = await saveReview({
                summary: saveSummary ?? '',
            });
            ShowSuccess(tRoot('actionsSuccessfulToast'));
            await KriOverviewApi.invalidateGetKRIReview(studyRef, datasetData!.id!, selectedPanelId!);

            reset({ summary: result.summary ?? '' });
            setIsEditing(false);
        } catch (e) {
            ShowError(t('review.errorSavingReview'));
            console.error('Failed to save KRI review.', e); // eslint-disable-line
        }
    };

    const onCancelHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        setIsEditing(false);
        reset();
    };

    return (
        <div className="review-rag-card">
            <div className="review-cards-content">
                <Card
                    className="text-area-cards"
                    title={`${t('review.comparisonDateTitle')}: ${
                        comparisonData?.dataCutDate ? `${comparisonData.dataCutDate}` : ''
                    }`}
                    data-testid="lastReviewCard"
                >
                    {comparisonData && selectedPanelId && kriReviewComparisonDataset?.summary ? (
                        <>
                            <div className="textarea-input">
                                <TextArea
                                    data-testid="review-summary"
                                    readOnly
                                    value={`${kriReviewComparisonDataset?.summary}`}
                                />
                            </div>
                            <div
                                className={`loading-overlay ${!isLoadingKriReviewComparisonDataset ? 'fade-out' : ''}`}
                            >
                                <Loading />
                            </div>
                            <div className="comparison-createdby-textarea">
                                {kriReviewComparisonDataset && kriReviewComparisonDataset.summary
                                    ? `Updated ${stringDateWithoutTimeFromUtc(
                                          kriReviewComparisonDataset.updated as string,
                                          'DD-MMM-YYYY',
                                      )} by
                     ${kriReviewComparisonDataset?.updatedByFirstName} ${kriReviewComparisonDataset?.updatedByLastName}`
                                    : ''}
                            </div>
                        </>
                    ) : (
                        <InfoField
                            message={
                                comparisonData && selectedPanelId
                                    ? t('review.noReview')
                                    : comparisonReviewDefaultMessage
                            }
                            type="Info"
                            layout="center"
                        />
                    )}
                </Card>
                <EditCard
                    className="text-area-cards"
                    title={`${t('review.datasetTitle')}: ${
                        datasetData?.dataCutDate ? `${datasetData.dataCutDate}` : ''
                    }`}
                    data-testid="current-review-card"
                    onEdit={() => setIsEditing(true)}
                    onCancel={onCancelHandler}
                    isEditMode={isEditing}
                    onSave={onSaveHandler}
                    disabled={isLoading || kriReviewDatasetInFocus === undefined || !canEdit}
                    saveDisabled={!isDirty}
                >
                    {selectedPanelId ? (
                        <>
                            <div className="textarea-input">
                                <TextAreaController
                                    control={control}
                                    name="summary"
                                    data-testid="review-summary"
                                    readOnly={!isEditing}
                                    maxLength={formValidationConfig.summary.maxLength}
                                />
                            </div>
                            <div className={`loading-overlay ${!isLoading ? 'fade-out' : ''}`}>
                                <Loading />
                            </div>
                            <div className="comparison-createdby-textarea">
                                {kriReviewDatasetInFocus && kriReviewDatasetInFocus.summary
                                    ? `Updated ${stringDateWithoutTimeFromUtc(
                                          kriReviewDatasetInFocus.updated as string,
                                          'DD-MMM-YYYY',
                                      )} by
                     ${kriReviewDatasetInFocus?.updatedByFirstName} ${kriReviewDatasetInFocus?.updatedByLastName}`
                                    : ''}
                            </div>
                        </>
                    ) : (
                        <InfoField message={datasetReviewDefaultMessage} type="Info" layout="center" />
                    )}
                </EditCard>
            </div>
        </div>
    );
};
